/**
 * Text message (SMS) reminders: the flag, the consent wording, phone numbers and reply keywords.
 * Shared by the opt-in field (client) and the sms callables + inbound webhook (server); copied to
 * functions/src/generated by scripts/syncFunctionsGenerated.mjs so both sides read ONE wording.
 *
 * The consent text is versioned. The client sends the version id AND the exact text it showed; the
 * server refuses an opt-in unless both match the current version here, then stores both. Changing
 * the wording means adding a NEW version id (never editing an old one), so every stored consent
 * record still names the words that person actually agreed to.
 *
 * Keep this file free of imports (pure, used by offline tests and both builds).
 */

/** Feature flag (featureFlags/global.flags.sms_notifications.state). Absent or 'off' = off everywhere. */
export const SMS_NOTIFICATIONS_FLAG = 'sms_notifications';
export const SMS_FLAG_LABEL = 'Text message reminders';
export const SMS_FLAG_DESCRIPTION =
  'Turns on the phone opt-in and the code check only. Reminder texts (interviews, orientation, document requests) are not wired to any event yet: nothing calls the sender. Keep off until they are wired and Twilio and A2P 10DLC registration are done (docs/sms-setup.md). While on without a live Twilio setup, texts are only logged (dry run), never sent.';

/**
 * Texting is "live" only when the global flag is plainly 'on'. 'rollout' needs a per-org override and
 * texts go to EMTs (who have no org), so rollout is not live for them. Signed-out pages (the SMS Terms,
 * the privacy section, the footer link, the sitemap) read this through the server
 * (functions/src/seoFiles.ts, /site-status.json) because featureFlags/global needs a signed-in reader.
 */
export function smsLiveFromGlobalFlags(flags: unknown): boolean {
  if (!flags || typeof flags !== 'object') return false;
  const def = (flags as Record<string, unknown>)[SMS_NOTIFICATIONS_FLAG];
  return Boolean(def && typeof def === 'object' && (def as { state?: unknown }).state === 'on');
}

/** Shown at the top of /sms-terms while texting is not live (2026-10-10 verification sweep). */
export const SMS_NOT_LIVE_NOTE =
  "Text messages from Arevina aren't available yet. No one can sign up for them and we don't send any. This page describes how they'll work once they launch.";
/** The same note as the first line of the privacy policy's Text Messaging section. */
export const SMS_NOT_LIVE_PRIVACY_NOTE =
  "Text messages from Arevina aren't available yet. No one can sign up for them and we don't send any. This section describes how they'll work once they launch.";

export const SMS_CONSENT_VERSION = 'sms-consent-2026-10-10b';

/**
 * What the opt-in checkbox says, word for word (2026-10-10b: names only the texts a template exists for
 * in ./templates.ts: interviews, orientation and document requests). Also the "opt-in description" for
 * 10DLC and toll-free registration. The checkbox is UNCHECKED by default and "Privacy Policy" and
 * "SMS Terms" are links to /privacy#sms and /sms-terms.
 */
export const SMS_CONSENT_TEXT =
  'I agree to receive text messages from Arevina about my interviews, orientation and document requests. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help. See our Privacy Policy and SMS Terms.';

/** Every consent wording ever shown, by version id. Old versions stay so stored records can be read back. */
export const SMS_CONSENT_VERSIONS: Readonly<Record<string, string>> = {
  'sms-consent-2026-10-09':
    'Text me reminders from Arevina at this number: interview and orientation reminders and document requests from employers I applied to or work for. A few messages a week at most. Msg and data rates may apply. Reply STOP to opt out, HELP for help. Agreeing is not required to use Arevina.',
  'sms-consent-2026-10-10':
    'I agree to receive text messages from Arevina about my applications, interviews and documents. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help. See our Privacy Policy and SMS Terms.',
  [SMS_CONSENT_VERSION]: SMS_CONSENT_TEXT,
};

/**
 * The program, as registered with carriers and shown on the SMS Terms page.
 * Names only the texts a template exists for (./templates.ts).
 * Sender wording matches functions/src/sms/provider.ts: every text goes out through Arevina's own
 * Twilio account (its Messaging Service, which may hold more than one number, or one from-number).
 * Employers never supply a sending number, so "never from an employer's own number" holds; "one
 * number" would not be guaranteed, so it is not said.
 */
export const SMS_PROGRAM_NAME = 'Arevina Hiring Updates';
export const SMS_PROGRAM_DESCRIPTION =
  'Text messages from Arevina to job seekers who opt in: interview confirmations and reminders, orientation reminders, and requests for documents from employers they applied to or work for. Every message is sent from Arevina\'s own messaging account, never from an employer\'s own number.';

/** profiles/{uid}.sms: the person's own view of their text opt-in. Written only by the server. */
export type SmsProfileState = {
  optedIn?: boolean;
  phoneLast4?: string | null;
  verifiedAt?: unknown;
  updatedAt?: unknown;
};

export type SmsConsentSource = 'signup' | 'settings';
export const SMS_CONSENT_SOURCES: readonly SmsConsentSource[] = ['signup', 'settings'];

function squash(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

/**
 * True only when the client named the CURRENT version and sent exactly its text (whitespace aside).
 * An old version, an unknown id or edited words are all refused: a new opt-in must be on today's text.
 */
export function isCurrentSmsConsent(version: unknown, text: unknown): boolean {
  if (version !== SMS_CONSENT_VERSION || typeof text !== 'string') return false;
  return squash(text) === squash(SMS_CONSENT_TEXT);
}

/* ------------------------------------------------------------------ phone numbers */

/**
 * A US mobile number in E.164 (+1XXXXXXXXXX), or null. Text reminders are US only (10DLC). Accepts
 * 10 digits or 11 starting with 1, any formatting. Area code and exchange must start with 2-9 (NANP).
 */
export function toUsE164(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  if (/[^\d\s()+.-]/.test(raw.trim())) return null;
  let digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) return null;
  if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return null;
  return `+1${digits}`;
}

/** Last 4 digits only, for anything a person or a log may see. */
export function phoneLast4(e164: unknown): string {
  const d = typeof e164 === 'string' ? e164.replace(/\D/g, '') : '';
  return d.length >= 4 ? d.slice(-4) : '';
}

/** "***-***-1234": what logs and the dry-run record store instead of the number. */
export function maskPhone(e164: unknown): string {
  const last4 = phoneLast4(e164);
  return last4 ? `***-***-${last4}` : '***';
}

/* ------------------------------------------------------------------ reply keywords */

export type SmsKeywordAction = 'opt_out' | 'opt_in' | 'help' | 'other';

/** Carrier-standard keywords (CTIA). Matched on the whole message, case and punctuation ignored. */
export const SMS_OPT_OUT_KEYWORDS: readonly string[] = ['STOP', 'STOPALL', 'UNSUBSCRIBE', 'CANCEL', 'END', 'QUIT', 'OPTOUT', 'REVOKE'];
export const SMS_OPT_IN_KEYWORDS: readonly string[] = ['START', 'YES', 'UNSTOP'];
export const SMS_HELP_KEYWORDS: readonly string[] = ['HELP', 'INFO'];

export function classifySmsKeyword(body: unknown): SmsKeywordAction {
  if (typeof body !== 'string') return 'other';
  const word = body.trim().toUpperCase().replace(/[^A-Z]/g, '');
  if (!word) return 'other';
  if (SMS_OPT_OUT_KEYWORDS.includes(word)) return 'opt_out';
  if (SMS_OPT_IN_KEYWORDS.includes(word)) return 'opt_in';
  if (SMS_HELP_KEYWORDS.includes(word)) return 'help';
  return 'other';
}

export const SMS_SUPPORT_EMAIL = 'support@arevina.com';

/** Replies to keywords. Plain ASCII so each fits one GSM-7 text. */
export const SMS_REPLIES = {
  help: `Arevina: texts on interviews, orientation and documents. Msg frequency varies. Msg & data rates may apply. Help: ${SMS_SUPPORT_EMAIL}. Reply STOP to opt out.`,
  optOut: 'Arevina: you will not get any more texts from us. Reply START to turn them back on.',
  optIn: 'Arevina: texts are back on. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.',
  other: 'Arevina: this number does not take replies. Open Arevina to answer your employer. Reply HELP for help, STOP to opt out.',
} as const;

/** Sent once when a number is confirmed (the opt-in confirmation carriers require). */
export const SMS_OPT_IN_CONFIRMATION =
  'Arevina: You are signed up for texts about your interviews, orientation and document requests. Msg frequency varies. Msg & data rates may apply. Reply HELP for help, STOP to opt out.';

/** The text that carries the 6-digit code. */
export function smsVerificationText(code: string): string {
  return `Arevina: your code is ${code}. It expires in 10 minutes. If you did not ask for it, ignore this text. Reply STOP to opt out.`;
}

/** Verification limits, shared so the UI can explain them. */
export const SMS_VERIFICATION_LIMITS = {
  codeTtlMinutes: 10,
  maxAttempts: 5,
  maxSendsPerDay: 5,
  minSecondsBetweenSends: 30,
} as const;
